/**
 * Generate an Instagram-story-style share card for a completed match.
 *
 * Returns a data URL (data:image/png;base64,…) ready to:
 *   - download (anchor.href = url + anchor.download)
 *   - or attach to navigator.share via Blob (Web Share Files level 2)
 *
 * Card is 1080 x 1920 (9:16) — the standard Instagram Story canvas.
 */

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_48907742-bee5-4d15-a4b2-afd889071bde/artifacts/479zxh1m_image.png';

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

const drawRoundedRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

/**
 * @param {Object}   opts
 * @param {string}   opts.winnerName
 * @param {string}   opts.loserName
 * @param {string}   opts.scoreLine          e.g. "21-15, 21-19"
 * @param {string}   [opts.photoDataUrl]     User-captured photo
 * @param {number}   [opts.winStreak]        Current win streak count
 * @param {string}   [opts.presetName]       e.g. "Classic 21"
 * @returns {Promise<string>} PNG data URL
 */
export async function buildStoryCard({
  winnerName,
  loserName,
  scoreLine,
  photoDataUrl,
  winStreak = 0,
  presetName = 'Match',
}) {
  const W = 1080, H = 1920;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Background: vibrant gradient
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, '#0EA5E9'); // sky-500
  bg.addColorStop(0.55, '#1E293B'); // slate-800
  bg.addColorStop(1, '#22C55E'); // green-500
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Logo (top)
  try {
    const logo = await loadImage(LOGO_URL);
    const logoH = 220;
    const logoW = (logo.width / logo.height) * logoH;
    ctx.drawImage(logo, (W - logoW) / 2, 80, logoW, logoH);
  } catch {
    /* logo missing — skip */
  }

  // Tagline
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.textAlign = 'center';
  ctx.font = '600 36px system-ui, sans-serif';
  ctx.fillText('SCORE • TRACK • DOMINATE', W / 2, 340);

  // Photo box (centered, 800x800 if available, else placeholder)
  const photoY = 420;
  const photoSize = 760;
  const photoX = (W - photoSize) / 2;
  drawRoundedRect(ctx, photoX, photoY, photoSize, photoSize, 32);
  ctx.fillStyle = 'rgba(15, 23, 42, 0.85)';
  ctx.fill();

  if (photoDataUrl) {
    try {
      const photo = await loadImage(photoDataUrl);
      ctx.save();
      drawRoundedRect(ctx, photoX, photoY, photoSize, photoSize, 32);
      ctx.clip();
      // Cover-fit the photo
      const scale = Math.max(photoSize / photo.width, photoSize / photo.height);
      const sw = photo.width * scale;
      const sh = photo.height * scale;
      ctx.drawImage(photo, photoX + (photoSize - sw) / 2, photoY + (photoSize - sh) / 2, sw, sh);
      ctx.restore();
    } catch {
      ctx.fillStyle = 'rgba(255,255,255,0.4)';
      ctx.font = '700 80px system-ui, sans-serif';
      ctx.fillText('🏸', W / 2, photoY + photoSize / 2 + 30);
    }
  } else {
    // Big trophy emoji as placeholder
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.font = '700 280px system-ui, sans-serif';
    ctx.fillText('🏆', W / 2, photoY + photoSize / 2 + 100);
  }

  // Winner banner (just below photo)
  const winY = photoY + photoSize + 90;
  ctx.fillStyle = '#FFFFFF';
  ctx.font = '800 56px system-ui, sans-serif';
  ctx.textAlign = 'center';
  const winnerText = `${winnerName.toUpperCase()} WINS`;
  ctx.fillText(winnerText, W / 2, winY);

  // Score line
  ctx.fillStyle = '#FCD34D'; // amber-300
  ctx.font = '700 64px ui-monospace, monospace';
  ctx.fillText(scoreLine, W / 2, winY + 90);

  // Loser + preset (subtle)
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  ctx.font = '500 32px system-ui, sans-serif';
  ctx.fillText(`vs ${loserName}  •  ${presetName}`, W / 2, winY + 145);

  // Win streak badge
  if (winStreak > 0) {
    const badgeW = 360;
    const badgeH = 90;
    const badgeX = (W - badgeW) / 2;
    const badgeY = winY + 190;
    drawRoundedRect(ctx, badgeX, badgeY, badgeW, badgeH, 45);
    ctx.fillStyle = 'rgba(249, 115, 22, 0.95)'; // orange-500
    ctx.fill();
    ctx.fillStyle = '#FFFFFF';
    ctx.font = '800 42px system-ui, sans-serif';
    ctx.fillText(`🔥 ${winStreak}-WIN STREAK`, W / 2, badgeY + 60);
  }

  // Footer CTA
  ctx.fillStyle = 'rgba(255,255,255,0.95)';
  ctx.font = '700 38px system-ui, sans-serif';
  ctx.fillText('play.theloveall.app', W / 2, H - 80);

  return canvas.toDataURL('image/png');
}

/**
 * Convert a data URL to a Blob.
 */
export function dataUrlToBlob(dataUrl) {
  const [meta, b64] = dataUrl.split(',');
  const mime = meta.match(/data:([^;]+)/)?.[1] || 'image/png';
  const bin = atob(b64);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/**
 * Trigger a download of the data URL with the given filename.
 */
export function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename || 'loveall-story.png';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
